// src/pages/profile-settings/doctor/PracticeTeamAccount.jsx
import React from "react";
import { Mail, Upload } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";

import CoverCropModal from "@/components/media/CoverCropModal.jsx";
import Button from "@/components/ui/Button.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import WorkspaceAccountContract, { WORKSPACE_ACCOUNT_GRID_CLASS } from "@/sot/layouts/WorkspaceAccountContract.jsx";
import AccountPasswordLifecyclePanel from "@/pages/profile-settings/AccountPasswordLifecyclePanel.jsx";
import AccountEmailChangeAction from "@/pages/profile-settings/AccountEmailChangeAction.jsx";
import supabase from "@/services/supabaseClient";
import { requestAccountPasswordReset } from "@/services/passwordRecoveryService.js";
import { updateOwnAccountDisplayName } from "@/services/accountIdentityService.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  getPharmacyMemberAccount,
  getPharmacyMemberPhotoUrl,
  removePharmacyMemberPhoto,
  updatePharmacyMemberAccount,
  uploadPharmacyMemberPhoto,
} from "@/services/pharmacyMemberAccountService.js";
import {
  getPracticeTeamMemberIdentity,
  getPracticeTeamRoleDescription,
  getPracticeTeamRoleLabel,
} from "./practiceTeamWorkspaceModel.js";
import {
  cachePracticeTeamMemberPhotoUrl,
  clearCachedPracticeTeamMemberPhotoUrl,
  readCachedPracticeTeamMemberPhotoUrl,
  usePracticeTeamAccess,
} from "./practiceTeamMemberAccess.js";
import PracticeTeamWorkspaceHeader from "./PracticeTeamWorkspaceHeader.jsx";

function safeText(value) {
  return String(value || "").trim();
}

function practiceTeamAccountErrorMessage(err, fallback) {
  const message = String(err?.message || "").trim();
  if (/row-level security policy/i.test(message) || /permission denied/i.test(message)) {
    return "Practice Team member image storage is waiting on the shared delegated member profile policy update.";
  }
  return message || fallback;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function InfoCard({ label, value, children }) {
  return (
    <div className="rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4">
      <div className="text-label-medium text-on-surface-var">{label}</div>
      {value ? <div className="mt-1 text-title-medium text-on-surface">{value}</div> : null}
      {children ? <div className="mt-2 text-body-medium text-on-surface-var">{children}</div> : null}
    </div>
  );
}

export default function PracticeTeamAccount() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { auth, invite, resolving, allowed } = usePracticeTeamAccess();
  const fileInputRef = React.useRef(null);
  const baseIdentity = React.useMemo(
    () => getPracticeTeamMemberIdentity(invite || {}, auth?.user || {}),
    [auth?.user, invite],
  );
  const roleLabel = invite?.role_label || getPracticeTeamRoleLabel(invite?.role);
  const roleDescription = getPracticeTeamRoleDescription(invite?.role);
  const userId = safeText(auth?.user?.id);
  const email = safeText(baseIdentity.email || auth?.user?.email).toLowerCase();
  const [accountLoading, setAccountLoading] = React.useState(Boolean(userId));
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [recoverySending, setRecoverySending] = React.useState(false);
  const [account, setAccount] = React.useState(null);
  const [fullName, setFullName] = React.useState(baseIdentity.displayName || "");
  const [photoUrl, setPhotoUrl] = React.useState(() => readCachedPracticeTeamMemberPhotoUrl({ userId }));
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [cropSource, setCropSource] = React.useState("");
  const [showCrop, setShowCrop] = React.useState(false);
  const hasPhoto = Boolean(photoUrl);
  const identity = React.useMemo(
    () => ({
      ...baseIdentity,
      displayName: safeText(fullName) || baseIdentity.displayName,
      avatarUrl: photoUrl || baseIdentity.avatarUrl,
    }),
    [baseIdentity, fullName, photoUrl],
  );

  React.useEffect(() => {
    if (!message) return undefined;
    const timeoutId = window.setTimeout(() => setMessage(""), 2400);
    return () => window.clearTimeout(timeoutId);
  }, [message]);

  const loadAccount = React.useCallback(async () => {
    if (!userId || !allowed) return;

    setAccountLoading(true);
    setError("");
    try {
      const nextAccount = await getPharmacyMemberAccount({
        userId,
        email,
        fullName: baseIdentity.displayName,
      });
      setAccount(nextAccount);
      setFullName(safeText(nextAccount?.full_name) || baseIdentity.displayName || "");

      if (safeText(nextAccount?.profile_photo_path)) {
        const cached = readCachedPracticeTeamMemberPhotoUrl({
          userId,
          profilePhotoPath: nextAccount.profile_photo_path,
        });
        if (cached) setPhotoUrl(cached);
        const signed = await getPharmacyMemberPhotoUrl(nextAccount.profile_photo_path);
        if (safeText(signed)) {
          cachePracticeTeamMemberPhotoUrl({
            userId,
            profilePhotoPath: nextAccount.profile_photo_path,
            photoUrl: signed,
          });
          setPhotoUrl(signed);
        }
      } else {
        clearCachedPracticeTeamMemberPhotoUrl({ userId });
        setPhotoUrl("");
      }
    } catch (err) {
      setError(practiceTeamAccountErrorMessage(err, "We couldn’t load your Practice Team account yet."));
    } finally {
      setAccountLoading(false);
    }
  }, [allowed, baseIdentity.displayName, email, userId]);

  React.useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  async function handleSave(event) {
    event?.preventDefault?.();
    if (!userId || saving) return;

    setSaving(true);
    setError("");
    setMessage("");
    try {
      const [nextAccount] = await Promise.all([
        updatePharmacyMemberAccount({ userId, fullName }),
        updateOwnAccountDisplayName(fullName),
      ]);
      setAccount(nextAccount);
      setFullName(safeText(nextAccount?.full_name));
      await auth?.refreshProfile?.();
      setMessage(t("settings.practiceTeam.account.saved", "Your Practice Team account details were saved."));
    } catch (err) {
      setError(practiceTeamAccountErrorMessage(
        err,
        t("settings.practiceTeam.account.saveError", "We couldn’t save your Practice Team account details."),
      ));
    } finally {
      setSaving(false);
    }
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (!file || !userId || uploading) return;

    setError("");
    setMessage("");
    try {
      const src = await fileToDataUrl(file);
      setCropSource(src);
      setShowCrop(true);
    } catch (err) {
      setError(String(err?.message || t("settings.practiceTeam.account.imagePrepareError", "We couldn’t prepare the Practice Team image.")));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleCropConfirm(blob) {
    if (!userId || uploading || !blob) return;

    setUploading(true);
    setError("");
    setMessage("");
    try {
      const contentType = blob?.type || "image/jpeg";
      const ext = contentType.includes("png")
        ? "png"
        : contentType.includes("webp")
          ? "webp"
          : "jpg";
      const croppedFile = new File([blob], `practice-team-member-avatar-${Date.now()}.${ext}`, {
        type: contentType,
      });

      const nextAccount = await uploadPharmacyMemberPhoto({
        userId,
        file: croppedFile,
        previousPath: safeText(account?.profile_photo_path),
      });
      setAccount(nextAccount);
      const signed = await getPharmacyMemberPhotoUrl(nextAccount?.profile_photo_path);
      cachePracticeTeamMemberPhotoUrl({
        userId,
        profilePhotoPath: nextAccount?.profile_photo_path,
        photoUrl: signed,
      });
      setPhotoUrl(signed);
      setMessage(t("settings.practiceTeam.account.saved", "Your Practice Team account details were saved."));
    } catch (err) {
      setError(practiceTeamAccountErrorMessage(
        err,
        t("settings.practiceTeam.account.imageUploadError", "We couldn’t upload the Practice Team image."),
      ));
    } finally {
      setUploading(false);
      setCropSource("");
      setShowCrop(false);
    }
  }

  async function handleRemovePhoto() {
    if (!userId || uploading || !safeText(account?.profile_photo_path)) return;

    setUploading(true);
    setError("");
    setMessage("");
    try {
      const nextAccount = await removePharmacyMemberPhoto({
        userId,
        profilePhotoPath: account?.profile_photo_path,
      });
      setAccount(nextAccount);
      clearCachedPracticeTeamMemberPhotoUrl({
        userId,
        profilePhotoPath: account?.profile_photo_path,
      });
      setPhotoUrl("");
      setMessage(t("settings.practiceTeam.account.saved", "Your Practice Team account details were saved."));
    } catch (err) {
      setError(practiceTeamAccountErrorMessage(
        err,
        t("settings.practiceTeam.account.imageRemoveError", "We couldn’t remove the Practice Team image."),
      ));
    } finally {
      setUploading(false);
    }
  }

  async function handleRecoveryLink() {
    if (!email || recoverySending) return;
    setRecoverySending(true);
    setError("");
    setMessage("");
    try {
      await requestAccountPasswordReset(email);
      setMessage("Secure recovery link sent.");
    } catch {
      setError("We couldn’t send a recovery link right now. Please try again.");
    } finally {
      setRecoverySending(false);
    }
  }

  if (resolving) {
    return <RouteFallback label="Loading Practice Team..." />;
  }

  if (!allowed) {
    // Do not hand a denied Practice Team route to the generic home resolver,
    // which can select an unrelated workspace such as Pharmacy.
    return <Navigate to="/practice-team/welcome" replace />;
  }

  return (
    <div className="min-h-dvh bg-[rgb(var(--afrimed-shell-background))] text-[rgb(var(--clr-on-surface))]">
      <PracticeTeamWorkspaceHeader section="account" invite={invite} identity={identity} />
      <SettingsScreenShell className="[--rail-pad:12px] lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
        <WorkspaceAccountContract
          title={t("settings.practiceTeam.account.title", "My account")}
          subtitle={t(
            "settings.practiceTeam.account.subtitle",
            "Your AfriMed™ Practice Team identity, role and assigned practice."
          )}
          onBack={() => navigate("/practice-team/welcome")}
        >

        <div className={WORKSPACE_ACCOUNT_GRID_CLASS}>
          <section className="order-1 lg:col-start-1 lg:row-start-1 lg:self-start">
            <form className="flex flex-col gap-6" onSubmit={handleSave}>
              <div className="px-1">
                <h2 className="text-title-large text-on-surface">
                  {t("settings.practiceTeam.account.memberProfile", "Member profile")}
                </h2>
                <p className="mt-1 text-body-medium text-on-surface-var">
                  {t(
                    "settings.practiceTeam.account.memberProfileCopy",
                    "Keep your Practice Team identity clear and role-safe across AfriMed™."
                  )}
                </p>
              </div>

              <TextField56
                label={t("settings.practiceTeam.account.fullName", "Full name")}
                autoComplete="name"
                value={fullName}
                onChange={setFullName}
                placeholder={t("settings.practiceTeam.account.fullNamePlaceholder", "Zola Ngcobo")}
                tone="surface"
                disabled={accountLoading || saving}
              />

              <TextField56
                label={t("settings.practiceTeam.account.workEmail", "Work email")}
                type="email"
                value={email}
                readOnly
                placeholder={email}
                tone="surface"
              />
              <AccountEmailChangeAction lane="practice_team" currentEmail={email} disabled={accountLoading || saving} />

              <div className="grid gap-3 md:grid-cols-2">
                <InfoCard label={t("settings.practiceTeam.account.role", "Assigned role")} value={roleLabel} />
                <InfoCard
                  label={t("settings.practiceTeam.account.practiceDoctor", "Practice • Doctor")}
                  value={identity.practiceName || t("settings.practiceTeam.account.practiceFallback", "Assigned practice")}
                />
              </div>

              <div className="px-1">
                <div className="text-title-medium text-on-surface">
                  {t("settings.practiceTeam.account.privilegeHandoff", "Privilege handoff")}
                </div>
                <p className="mt-2 text-body-medium text-on-surface-var">
                  {roleDescription || t(
                    "settings.practiceTeam.account.privilegeCopy",
                    "Practice Team members work on behalf of a clinician under delegated permissions. Clinical, prescription, billing and patient-record capabilities remain locked until explicitly enabled."
                  )}
                </p>
              </div>

              {error ? (
                <div className="rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-on-surface">
                  {error}
                </div>
              ) : null}

              {message ? (
                <div className="rounded-[12px] bg-[var(--state-primary-12)] px-4 py-3 text-body-medium text-on-surface">
                  {message}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-3 px-1">
                <Button
                  type="submit"
                  disabled={accountLoading || saving || !safeText(fullName)}
                >
                  {saving ? t("common.saving", "Saving…") : t("common.saveChanges", "Save changes")}
                </Button>
              </div>
            </form>
          </section>

          <section className="order-2 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start">
            <div className="flex flex-col gap-5">
              <div>
                <h2 className="text-title-large text-on-surface">
                  {t("settings.practiceTeam.account.profileImage", "Profile image")}
                </h2>
                <p className="mt-1 text-body-medium text-on-surface-var">
                  {t(
                    "settings.practiceTeam.account.profileImageCopy",
                    "Stored in the delegated member image lane and preferred by the Practice Team header first."
                  )}
                </p>
              </div>

              <div className="flex flex-col items-center gap-4">
                <AvatarThumb68
                  src={photoUrl}
                  alt="Practice Team member profile"
                  shape="circle"
                  role="patient"
                  fallbackSrc="/icons/circular-avatar-placeholder.svg"
                  className="aspect-square"
                  style={{ width: 104, height: 104 }}
                  imgClassName={hasPhoto ? "object-cover p-0" : "object-contain p-[6px]"}
                />

                <div className="w-full space-y-3">
                  <Button
                    type="button"
                    variant="tonal"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={accountLoading || uploading}
                    className="w-full gap-2"
                  >
                    <Upload className="h-5 w-5 text-primary" aria-hidden />
                    <span>{uploading ? t("common.uploading", "Uploading…") : t("common.uploadImage", "Upload image")}</span>
                  </Button>

                  <Button
                    type="button"
                    variant="criticalTonal"
                    onClick={handleRemovePhoto}
                    disabled={accountLoading || uploading || !safeText(account?.profile_photo_path)}
                    className="w-full gap-2"
                  >
                    <img
                      src="/icons/doctor/delete_critical.svg"
                      alt=""
                      aria-hidden="true"
                      className="h-5 w-5"
                    />
                    {t("common.removeImage", "Remove image")}
                  </Button>
                </div>
              </div>

              <p className="text-center text-body-small text-on-surface-var">
                {t("pharmacy.account.imageSupport", "Supported: JPEG, PNG, WebP up to 5MB.")}
              </p>
            </div>
          </section>
          <section className="order-3 lg:col-start-1 lg:row-start-2">
          <AccountPasswordLifecyclePanel
            context="settings"
            intent="create"
            hasPasswordOverride={false}
            userEmailOverride={email}
            titleOverride="Password and sign-in"
            copyOverride="Use your password for normal Practice Team sign-in. Secure links remain available for first-time setup and recovery."
            createSignOutNoticeOverride={null}
            submitLabelOverride="Set or change password"
            enableResetHandoff={false}
            resetBasePath="/practice-team/account"
            loginSecurityPath="/practice-team/account"
            showCopyPasswordAction
            submitRowClassName="flex w-full flex-col gap-3 sm:flex-row sm:items-center"
            titleClassName="text-title-large text-on-surface"
            className="space-y-0"
            secondarySubmitAction={
              <Button
                type="button"
                variant="tonal"
                onClick={handleRecoveryLink}
                disabled={accountLoading || recoverySending || !email}
                loading={recoverySending}
                className="min-h-10 gap-2 sm:flex-1"
                fullWidth
              >
                <Mail className="h-4 w-4" aria-hidden />
                Send recovery link
              </Button>
            }
          />
        </section>
        </div>
        </WorkspaceAccountContract>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileChange}
          className="hidden"
        />

        {showCrop && cropSource ? (
          <CoverCropModal
            src={cropSource}
            aspect={1 / 1}
            onCancel={() => {
              setShowCrop(false);
              setCropSource("");
            }}
            onConfirm={handleCropConfirm}
          />
        ) : null}
      </SettingsScreenShell>
    </div>
  );
}
